import {observable, autorun, computed} from 'mobx';
import * as LoadLevel from './LoadLevel';
import Job from './Job';
import MachineConfigParameter from './MachineConfigParameter';
import SimCard from './SimCard';

// For now this is just a blind copy of the rows from the database.
function createMachineFromDBRow(row) {
  //console.log("createMachineFromDBRow",row);
  var m = new Machine();
  m.id=row.id;
  m.name=row.name;
  m.model=row.model;
  m.serial_number_dib=row.serial_number_dib;
  m.id_customer=row.id_customer;
  m.software_version=row.software_version;
  m.micro_controller_software_version=row.micro_controller_software_version;
  m.iccid=row.iccid;
  // This is all the info we need to present a machine in a list
  m.load_level=LoadLevel.LIST;
  m.action = row.action;
  m.supported_features = row.supported_features;
  if(row.last_heartbeat) {
    m.last_heartbeat = new Date(row.last_heartbeat);
  }
  // If there is a last job for this machine:
  if(row.last_job_start_time) {
    // This is a bit of an issue.  What if there is already a job object in
    // the AppModel.jobList for this job.
    m.last_job = new Job();
    m.last_job.id_machine = m.id;
    m.last_job.machine_name = m.name;
    m.last_job.start_time = new Date(row.last_job_start_time);
    m.last_job.user_id = row.last_job_user_id;
    m.last_job.location_name = row.last_job_location;
    m.last_job.id_location = row.last_job_id_location;
    if(row.last_job_end_time)
      m.last_job.end_time = new Date(row.last_job_end_time);
      m.last_job.status = row.last_job_status;
      // Knowing machine_id and start_time for a job is at least ID-level of loaded
      m.last_job.load_level = LoadLevel.ID;
  }
  if(row.configuration) {
    m.configuration = row.configuration.map(
      MachineConfigParameter.createFromDBRow
    );
  }
  if(row.running_time) {
    row.running_time.forEach(rt => {
      switch (rt.component) {
        case "Compressor":
          m.running_time_compressor = rt.runtime;
          m.last_reset_timestamp_compressor = rt.last_reset_timestamp;
          break;
        case "LiquidPump":
          m.running_time_liquid_pump = rt.runtime;
          m.last_reset_timestamp_liquid_pump = rt.last_reset_timestamp;
          break;
        case "AirFilter":
          m.running_time_air_filter = rt.runtime;
          m.last_reset_timestamp_air_filter = rt.last_reset_timestamp;
          break;                      
        default:
          break;
      }
    })
  }
  //console.log("Looking at last measures");
  if(row.lastMeasurements) {
    let lastRecentMeasures = new Map();
    // Check if any of the last measurements are fresh enough to show
    let last_reading_ts = null;
    row.lastMeasurements.forEach( lastm => {
      // Convert the timestamp to a js Date object
      let ts = new Date(lastm.ts);
      lastRecentMeasures.set(lastm.id_sensor, lastm);
      if(last_reading_ts==null || last_reading_ts < ts) {
        last_reading_ts=ts;
      }
    })
    m.last_reading_ts = last_reading_ts;
    if(lastRecentMeasures.size>0) {
      m.last_measurements = lastRecentMeasures;
    }
  }
  return m;
}

class Machine {
  @observable id;
  @observable name;
  @observable model;
  @observable serial_number;
  @observable serial_number_dib;
  @observable id_customer;
  @observable software_version;
  @observable micro_controller_software_version;
  @observable iccid;
  @observable simcard;
  @observable sim_activation_status;
  // Fields that should be filled in when fetching machine data to show
  // activity.  E.g. might not be filled in when listing machine hardware and
  // software version numbers
  @observable last_job = null;
  @observable last_job_start_time = null;
  @observable last_job_end_time = null;
  @observable last_job_status = null;

  // This is an array of the last measurement for each sensor reported for
  // this machine. It will only be set if machine_details are loaded
  @observable last_measurements = null;
  // The timestamp of the last reading received for this machine:
  // It should be updated when new job-data is pulled.
  @observable last_reading_ts = null;
  // These are status fields that represent the status of loading data
  // the server for this machine

  @observable configuration = [];

  @observable load_level;
  @observable loading;

  @observable history // customer_machine history for a given machine
  @observable last_heartbeat = null;
  @observable running_time_compressor = null;
  @observable last_reset_timestamp_compressor = null;
  @observable running_time_liquid_pump = null;
  @observable last_reset_timestamp_liquid_pump = null;
  @observable running_time_air_filter = null;
  @observable last_reset_timestamp_air_filter = null;


  constructor() {
    this.reset();
  }
  // In use when registering a new machine :
   reset() {
    this.id=null;
    this.name=null;
    this.model=null;
    this.serial_number=null;
    this.serial_number_dib=null;
    this.id_customer=null;
    this.software_version="0.0.0";
    this.micro_controller_software_version="0.0.0";
    this.load_level=LoadLevel.NONE;
    this.loading=false;
    this.last_job = null;
    this.last_reading_ts = null;
    this.last_measurements = null;
    this.iccid=null;
    this.simcard = new SimCard();
    this.sim_activation_status=null;
    this.history=null;
    this.last_heartbeat = null;
    this.running_time_compressor = null;
    this.last_reset_timestamp_compressor = null;
    this.running_time_liquid_pump = null;
    this.last_reset_timestamp_liquid_pump = null;
    this.running_time_air_filter = null;
    this.last_reset_timestamp_air_filter = null;
  }
  // Helper methods
  @computed get isJobRunning() {
    if(!this.last_job) return false;
    return this.last_job.running;
  }
  // Validation helper methods for registering new machine :
  @computed get isValid() {
    return  this.isIdValid &&
            this.isNameValid &&
            this.isModelValid &&
            this.isCustomerIdValid;
  }
  @computed get isAssignmentValid() {
    return  this.isIdValid &&
            this.isNameValid &&
            this.isCustomerIdValid;
  }
  @computed get isIdValid() {
    return !(this.id==null || this.id=="");
  }
  @computed get isNameValid() {
    return this.name > "";
  }
  @computed get isModelValid() {
    return this.model > "";
  }
  @computed get isCustomerIdValid() {
    return this.id_customer > "";
  }

  @computed get hasValidSimCard() {
    if (this.iccid == null) return false;
    // The ICCID field has to be a string (20-ish digit number otherwise)
    if(typeof(this.iccid) !='string') return false;
    if (this.iccid.trim() == "") return false;
    // If there are fewer than 10 digits, something is wrong
    if(this.iccid.length<10) return false;
    // it can only contain digits:
    return this.iccid.trim().match(/^[0-9]+$/) != null;
  }


  // Make a copy (to insert into machineList after sent to server)
  clone() {
    var res = new Machine();
    Object.assign(res,this);
    return res;
  }

  // If we fetch a machine record and want to update an existing object
  updateFrom(otherMachine) {
    // The fields to copy from the other machine object
    this.name = otherMachine.name;
    this.model = otherMachine.model;
    this.serial_number = otherMachine.serial_number;
    this.serial_number_dib = otherMachine.serial_number_dib;
    this.id_customer = otherMachine.id_customer;
    this.software_version = otherMachine.software_version;
    this.micro_controller_software_version =
      otherMachine.micro_controller_software_version;
    this.iccid = otherMachine.iccid;
    this.sim_activation_status = otherMachine.sim_activation_status;
    this.load_level = otherMachine.load_level;
    this.last_measurements = otherMachine.last_measurements;
    this.last_reading_ts = otherMachine.last_reading_ts;
    // We are copying reference to a config array here, with the assumption
    this.configuration = otherMachine.configuration;
    this.last_heartbeat = otherMachine.last_heartbeat;
    this.running_time_compressor = otherMachine.running_time_compressor;
    this.running_time_liquid_pump = otherMachine.running_time_liquid_pump;
    this.running_time_air_filter = otherMachine.running_time_air_filter;
    this.last_reset_timestamp_compressor = otherMachine.last_reset_timestamp_compressor;
    this.last_reset_timestamp_liquid_pump = otherMachine.last_reset_timestamp_liquid_pump;
    this.last_reset_timestamp_air_filter = otherMachine.last_reset_timestamp_air_filter;
  }

}

// Make utility method available from Class (function)
Machine.createFromDBRow=createMachineFromDBRow;

export default Machine;



/** WEBPACK FOOTER **
 ** ./src/DataModel/Machine.js
 **/